import { getThreadedWasmSupport } from "../physics/threadedWasmSupport";

const WASM_SIMD_PROBE = new Uint8Array([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, 0x01, 0x04, 0x01, 0x60, 0x00, 0x00, 0x03, 0x02,
  0x01, 0x00, 0x0a, 0x09, 0x01, 0x07, 0x00, 0x41, 0x00, 0xfd, 0x0f, 0x1a, 0x0b,
]);

export type BrowserCapabilityId =
  | "modernJs"
  | "webgl2"
  | "resizeObserver"
  | "intersectionObserver"
  | "indexedDB"
  | "webCrypto"
  | "clipboard"
  | "wasmSimd"
  | "physicsIsolation";

export type BrowserCapabilityStatus = "supported" | "degraded" | "unsupported";

export type BrowserCapabilityReport = {
  id: BrowserCapabilityId;
  label: string;
  ok: boolean;
  status: BrowserCapabilityStatus;
  detail: string;
};

type RuntimeGlobals = typeof globalThis & {
  document?: Document;
  navigator?: Navigator;
};

export const BROWSER_CAPABILITY_ORDER: BrowserCapabilityId[] = [
  "modernJs",
  "webgl2",
  "resizeObserver",
  "intersectionObserver",
  "indexedDB",
  "webCrypto",
  "clipboard",
  "wasmSimd",
  "physicsIsolation",
];

function supportsWasmSimd(runtimeGlobals: RuntimeGlobals): boolean {
  try {
    return (
      typeof runtimeGlobals.WebAssembly !== "undefined" &&
      runtimeGlobals.WebAssembly.validate(WASM_SIMD_PROBE)
    );
  } catch {
    return false;
  }
}

function supportsWebGl2(runtimeGlobals: RuntimeGlobals): boolean {
  try {
    const canvas = runtimeGlobals.document?.createElement?.("canvas");
    const context = canvas?.getContext?.("webgl2", { failIfMajorPerformanceCaveat: true });
    return Boolean(context);
  } catch {
    return false;
  }
}

function hasIndexedDb(runtimeGlobals: RuntimeGlobals): boolean {
  try {
    return typeof runtimeGlobals.indexedDB !== "undefined";
  } catch {
    return false;
  }
}

function hasWebCrypto(runtimeGlobals: RuntimeGlobals): boolean {
  return typeof runtimeGlobals.crypto?.subtle?.digest === "function";
}

function hasClipboardWrite(runtimeGlobals: RuntimeGlobals): boolean {
  return typeof runtimeGlobals.navigator?.clipboard?.writeText === "function";
}

function capability(
  id: BrowserCapabilityId,
  label: string,
  ok: boolean,
  detail: string,
  unsupportedDetail: string,
  degraded = false
): BrowserCapabilityReport {
  return {
    id,
    label,
    ok,
    status: ok ? "supported" : degraded ? "degraded" : "unsupported",
    detail: ok ? detail : unsupportedDetail,
  };
}

export function collectBrowserRuntimeCapabilities(
  runtimeGlobals: RuntimeGlobals = globalThis
): BrowserCapabilityReport[] {
  const threadedWasmSupport = getThreadedWasmSupport(runtimeGlobals);
  const webgl2 = supportsWebGl2(runtimeGlobals);
  const indexedDB = hasIndexedDb(runtimeGlobals);
  const webCrypto = hasWebCrypto(runtimeGlobals);
  const clipboard = hasClipboardWrite(runtimeGlobals);
  const wasmSimd = supportsWasmSimd(runtimeGlobals);

  return [
    capability(
      "modernJs",
      "Modern JS modules",
      true,
      "This ESM diagnostics module loaded successfully.",
      "This browser cannot load Critter's Vite module bundle."
    ),
    capability(
      "webgl2",
      "WebGL2",
      webgl2,
      "3D viewer GPU context is available.",
      "The 3D viewer may fail because WebGL2 is unavailable."
    ),
    capability(
      "resizeObserver",
      "ResizeObserver",
      typeof runtimeGlobals.ResizeObserver !== "undefined",
      "Panel layout observers are available.",
      "Some panels may fail to resize or measure correctly.",
      true
    ),
    capability(
      "intersectionObserver",
      "IntersectionObserver",
      typeof runtimeGlobals.IntersectionObserver !== "undefined",
      "Lazy thumbnail visibility observers are available.",
      "Some thumbnails may not update correctly.",
      true
    ),
    capability(
      "indexedDB",
      "IndexedDB",
      indexedDB,
      "Local asset persistence API is exposed.",
      "Uploaded asset persistence may be session-only.",
      true
    ),
    capability(
      "webCrypto",
      "Web Crypto",
      webCrypto,
      "crypto.subtle hashing APIs are available.",
      "Some project, export, import, or asset hashing workflows may fail.",
      true
    ),
    capability(
      "clipboard",
      "Clipboard",
      clipboard,
      "Clipboard write API is available.",
      "Copy report actions may fail.",
      true
    ),
    capability(
      "wasmSimd",
      "WASM SIMD",
      wasmSimd,
      "Browser-trained model inference can use the SIMD runtime.",
      "Browser-trained model inference is unavailable without WebAssembly SIMD.",
      true
    ),
    capability(
      "physicsIsolation",
      "Physics isolation",
      threadedWasmSupport.ok,
      "WebAssembly, SharedArrayBuffer, and cross-origin isolation are available.",
      threadedWasmSupport.ok ? "" : threadedWasmSupport.message
    ),
  ];
}

export function getBrowserRuntimeCapabilityMap(
  capabilities: BrowserCapabilityReport[]
): Record<BrowserCapabilityId, boolean> {
  return Object.fromEntries(capabilities.map((entry) => [entry.id, entry.ok])) as Record<
    BrowserCapabilityId,
    boolean
  >;
}
